import { useState, useEffect } from 'react';
import { Briefcase, Target, TrendingUp, Info } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import portfolioService from '@services/portfolio.service';
import ProfessionalResume from './ProfessionalResume';

const ProfessionalPortfolio = () => {
  const {
    professionalTier,
    industryFocus,
    experienceLevel,
    careerSwitchPreferences,
    updateProfessionalTier,
    updateIndustryFocus,
    updateExperienceLevel,
    toggleCareerSwitchPreference,
  } = usePortfolio();

  const [customPreference, setCustomPreference] = useState('');
  const [industryOptions, setIndustryOptions] = useState([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      setLoadingIndustries(true);
      const response = await portfolioService.getJobProfiles({});
      const profiles = response?.data?.data || [];
      
      // Extract unique industries
      const uniqueIndustries = [...new Set(profiles.map(p => p.industry))];
      setIndustryOptions(uniqueIndustries.sort());
    } catch (error) {
      console.error('Error fetching industries:', error);
      // Fallback to default industries
      setIndustryOptions(['Information Technology', 'Operations', 'Research & Development']);
    } finally {
      setLoadingIndustries(false);
    }
  };

  const professionalTierOptions = [
    { value: 'Early Career', label: 'Early Career', description: 'Building foundational skills and experience' },
    { value: 'Mid Career', label: 'Mid Career', description: 'Established professional with proven expertise' },
    { value: 'Senior Professional', label: 'Senior Professional', description: 'Leadership and strategic roles' },
    { value: 'Career Switch', label: 'Career Switch', description: 'Transitioning to a new career path' }
  ];

  const experienceLevelOptions = [
    { value: '< 3 years', label: '< 3 years' },
    { value: '3-5 years', label: '3-5 years' },
    { value: '5-10 years', label: '5-10 years' },
    { value: '> 10 years', label: '> 10 years' }
  ];

  const careerSwitchReasons = [
    'Seeking greater alignment with long-term career interests',
    'Exploring opportunities for broader domain exposure and professional growth',
    'Looking for enhanced compensation and advancement opportunities',
    'Intending to achieve improved work-life integration and flexibility',
    'Interested in transitioning to a role with stronger learning and development potential'
  ];

  const handleAddCustomPreference = () => {
    if (customPreference.trim() && !careerSwitchPreferences.includes(customPreference.trim())) {
      toggleCareerSwitchPreference(customPreference.trim());
      setCustomPreference('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Professional Tier Selection */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-blue-100">
          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-600 flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Professional Tier
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select your current professional level
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {professionalTierOptions.map((option) => {
              const isSelected = professionalTier === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateProfessionalTier(option.value)}
                  className={`text-left p-3 sm:p-4 rounded-xl border transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50/60 shadow-md ring-2 ring-blue-200'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                        {option.label}
                      </h4>
                      <p className="text-[11px] sm:text-xs text-gray-600 mt-1">
                        {option.description}
                      </p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'bg-blue-500 border border-blue-500'
                          : 'bg-white border border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <svg
                          className="w-3 h-3 text-white"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Career Switch Preferences - Only show if Career Switch is selected */}
      {professionalTier === 'Career Switch' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-amber-100">
            <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-600 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                Career Switch Preferences
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                Select your reasons for career transition
              </p>
            </div>
          </div>

          <div className="p-3 sm:p-4 space-y-3">
            <div className="space-y-2">
              {careerSwitchReasons.map((reason, index) => {
                const isSelected = careerSwitchPreferences.includes(reason);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleCareerSwitchPreference(reason)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'border-amber-500 bg-amber-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                          isSelected
                            ? 'bg-amber-500 border border-amber-500'
                            : 'bg-white border border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm text-gray-900 flex-1">
                        {reason}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Custom Preference Input */}
            <div className="border-t border-gray-100 pt-3">
              <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                Optional: Please specify any additional reason or career objective
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customPreference}
                  onChange={(e) => setCustomPreference(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddCustomPreference();
                    }
                  }}
                  placeholder="Enter your custom preference..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={handleAddCustomPreference}
                  disabled={!customPreference.trim()}
                  className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Selected Preferences Summary */}
            {careerSwitchPreferences.length > 0 && (
              <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-800">
                    Selected Preferences ({careerSwitchPreferences.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => careerSwitchPreferences.forEach(pref => toggleCareerSwitchPreference(pref))}
                    className="text-xs font-semibold text-amber-600 hover:text-amber-700"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {careerSwitchPreferences.map((pref, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs text-amber-700"
                    >
                      <Info className="w-3 h-3" />
                      {pref.length > 50 ? `${pref.substring(0, 50)}...` : pref}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Industry Focus Selection */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-purple-100">
          <div className="w-8 h-8 rounded-lg bg-purple-100 border border-purple-600 flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-4 h-4 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Industry Focus
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select your primary industry of interest
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {industryOptions.map((industry) => {
              const isSelected = industryFocus === industry;
              return (
                <button
                  key={industry}
                  type="button"
                  onClick={() => updateIndustryFocus(industry)}
                  className={`p-3 sm:p-4 rounded-lg border text-center transition-all ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 shadow-md ring-2 ring-purple-200'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                  }`}
                >
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    {industry}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Experience Level */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-green-100">
          <div className="w-8 h-8 rounded-lg bg-green-100 border border-green-600 flex items-center justify-center flex-shrink-0">
            <Target className="w-4 h-4 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Experience Level
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select your total years of professional experience
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {experienceLevelOptions.map((option) => {
              const isSelected = experienceLevel === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateExperienceLevel(option.value)}
                  className={`p-3 sm:p-4 rounded-lg border text-center transition-all ${
                    isSelected
                      ? 'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200'
                      : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-sm'
                  }`}
                >
                  <div className="text-sm sm:text-base font-semibold text-gray-900">
                    {option.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Resume Section */}
      
    </div>
  );
};

export default ProfessionalPortfolio;
